import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";

import type { FlowSuggestion, SuggestionValue } from "../../../api/flowTypes";

/**
 * 「补充建议」:原采集问答里缺的事实(电话 / 地址 / 资质 …)。生成时缺的不编造、不阻断,在这里补;
 * 填了点「更新官网」→ 写回资料(复用 collect/answers)并「更新官网」(generate mode=refresh,不扣费;进度由调用方画)。
 * ★ 只发填了的那几条;一条没填按钮禁用。
 */
export function SuggestionsPanel({
  items,
  error,
  busy,
  onSubmit,
}: {
  items: FlowSuggestion[] | null;
  error?: string | null;
  busy?: boolean;
  onSubmit: (values: SuggestionValue[]) => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => {
    setValues({});
  }, [items]);

  const answers: SuggestionValue[] = (items ?? [])
    .map((i) => ({ path: i.path, value: (values[i.path] ?? "").trim() }))
    .filter((a) => a.value !== "");

  return (
    <section
      aria-label="补充建议"
      className={cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS)}
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground">补充建议</h2>
        <p className="mt-0.5 text-2xs text-muted-foreground">
          资料里没找到的信息不会编造,对应位置先隐藏或留占位。补上后点「更新官网」。
        </p>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : items === null ? (
        <p className="text-xs text-muted-foreground">正在读取…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">资料都齐了,没有要补的。</p>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((i) => (
              <li key={i.path} className="space-y-1" data-suggestion={i.path}>
                <label className="text-xs font-medium text-foreground" htmlFor={`sg-${i.path}`}>
                  {i.label}
                  {i.kind === "conflict" && (
                    <span className="ml-1 text-2xs font-normal text-warning">资料说法不一致</span>
                  )}
                </label>
                <Input
                  id={`sg-${i.path}`}
                  value={values[i.path] ?? ""}
                  disabled={busy}
                  placeholder={i.hint ?? undefined}
                  onChange={(e) => setValues((v) => ({ ...v, [i.path]: e.target.value }))}
                />
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            disabled={busy || answers.length === 0}
            onClick={() => onSubmit(answers)}
          >
            {busy ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            更新官网
          </Button>
        </>
      )}
    </section>
  );
}
